import {
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import React, { useEffect, useState } from "react";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { CommonButton } from "../../../../common/CommonButton";
import CancelEventDetail from "./CancelEventDetail";
import { AppImages } from "../../../../utility/AppImages";
import CancelEventReason from "./CancelEventReason";

export default function CancelEventPopup(props) {
  const {
    showModal,
    setShowModal,
    selectedReason,
    setSelectedReason,
    customReason,
    setCustomReason,
    message,
    setMessage,
    handleCancelEvent,
    isCancelling,
    accessToken,
    eventID,
    viewers = [], // Array of signed up viewers
    totalViewers = 0, // Total viewer count
  } = props;
  const styles = useStyles();
  const [selectReasonDropdown, setSelectReasonDropdown] = useState(false);
  const [showMessageError, setShowMessageError] = useState(false);
  const deviceSize = useDeviceSize();
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  const onCloseClick = () => {
    setShowModal(false);
    setShowMessageError(false);
  };

  const onConfirmClick = () => {
    if (!message?.trim()) {
      setShowMessageError(true);
      return;
    }
    setShowMessageError(false);
    handleCancelEvent();
  };

  return (
    <Modal
      animationType={isMobile ? "slide" : "fade"}
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <View style={styles.mainView}>
          {/* Drag handle for mobile */}
          {isMobile && (
            <View style={styles.dragHandle}>
              <View style={styles.dragIndicator} />
            </View>
          )}

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {isMobile ? (
              // Mobile layout - stats first, then form
              <>
                <CancelEventDetail
                  onCloseClick={onCloseClick}
                  onConfirm={handleCancelEvent}
                  isCancelling={isCancelling}
                  accessToken={accessToken}
                  eventID={eventID}
                  selectedReason={selectedReason}
                  message={message}
                  isMobile={true}
                  showButtons={false}
                  viewers={viewers}
                  totalViewers={totalViewers}
                />
                <CancelEventReason
                  selectReasonDropdown={selectReasonDropdown}
                  setSelectReasonDropdown={setSelectReasonDropdown}
                  selectedReason={selectedReason}
                  setSelectedReason={setSelectedReason}
                  customReason={customReason}
                  setCustomReason={setCustomReason}
                  message={message}
                  setMessage={setMessage}
                  isMobile={true}
                  showMessageError={showMessageError}
                />
              </>
            ) : (
              // Web layout - side by side
              <View style={styles.webLayout}>
                <CancelEventReason
                  selectReasonDropdown={selectReasonDropdown}
                  setSelectReasonDropdown={setSelectReasonDropdown}
                  selectedReason={selectedReason}
                  setSelectedReason={setSelectedReason}
                  customReason={customReason}
                  setCustomReason={setCustomReason}
                  message={message}
                  setMessage={setMessage}
                  isMobile={false}
                  showMessageError={showMessageError}
                />
                <CancelEventDetail
                  onCloseClick={onCloseClick}
                  onConfirm={onConfirmClick}
                  isCancelling={isCancelling}
                  accessToken={accessToken}
                  eventID={eventID}
                  selectedReason={selectedReason}
                  message={message}
                  isMobile={false}
                  showButtons={true}
                  viewers={viewers}
                  totalViewers={totalViewers}
                />
              </View>
            )}
          </ScrollView>

          {/* Sticky buttons for mobile */}
          {isMobile && (
            <View style={styles.mobileButtonContainer}>
              <CommonButton
                title="Cancel"
                onTap={onCloseClick}
                buttonStyle={styles.cancelButton}
                textStyle={styles.cancelButtonText}
                disabled={isCancelling}
              />
              <CommonButton
                title={isCancelling ? "Cancelling..." : "Confirm Cancellation"}
                onTap={onConfirmClick}
                buttonStyle={styles.confirmButton}
                textStyle={styles.confirmButtonText}
                disabled={isCancelling}
              />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      width: "70%",
      maxWidth: 900,
      maxHeight: "90%",
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      backgroundColor: "#111827",
      padding: 35,
    },
    scrollView: {
      width: "100%",
    },
    scrollContent: {
      flexGrow: 1,
    },
    webLayout: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "stretch",
      gap: 40,
      flex: 1,
      minHeight: 350,
    },
    dragHandle: {
      alignItems: "center",
      paddingVertical: 10,
    },
    dragIndicator: {
      width: 40,
      height: 4,
      backgroundColor: "#374151",
      borderRadius: 2,
    },
    mobileButtonContainer: {
      flexDirection: "row",
      gap: 12,
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: "#374151",
      marginTop: 16,
    },
    cancelButton: {
      flex: 0.4,
      height: 48,
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: "#FFFFFF",
      borderRadius: 6,
      shadowOpacity: 0,
      justifyContent: "center",
      alignItems: "center",
    },
    cancelButtonText: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      color: "#FFFFFF",
    },
    confirmButton: {
      flex: 0.6,
      height: 48,
      backgroundColor: "#DC2626",
      borderRadius: 6,
      shadowOpacity: 0,
      justifyContent: "center",
      alignItems: "center",
    },
    confirmButtonText: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      color: "#FFFFFF",
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      mainView: {
        width: "60%",
      },
    },
    [DEVICE_SIZES.MD]: {
      mainView: {
        width: "80%",
      },
    },
    [DEVICE_SIZES.SM]: {
      container: {
        justifyContent: "flex-end",
      },
      mainView: {
        width: "100%",
        maxWidth: "100%",
        height: "85%",
        borderRadius: 0,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomWidth: 0,
        padding: 16,
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        justifyContent: "flex-end",
      },
      mainView: {
        width: "100%",
        maxWidth: "100%",
        height: "85%",
        borderRadius: 0,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomWidth: 0,
        padding: 16,
      },
    },
  }
);
